import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import useHighlightParam from "@/hooks/useHighlightParam";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DocPreviewDialog } from "@/components/DocPreviewDialog";
import { DocThumb } from "@/components/DocThumb";
import { toast } from "sonner";
import { FileSignature, Plus, Trash2, Edit3, Loader2, UploadCloud, Eye, Building2, Sparkles, CalendarClock, AlertTriangle, CheckCircle2, XCircle, Ban, Undo2, Landmark } from "lucide-react";

const TYPES = { miete: "Miete", leasing: "Leasing", versicherung: "Versicherung", abo: "Abo / Lizenz", arbeit: "Arbeitsvertrag", wartung: "Wartung / Service", darlehen: "Darlehen", lieferung: "Lieferung / Kauf", sonstiges: "Sonstiges" };
const INTERVALS = { monthly: "pro Monat", quarterly: "pro Quartal", yearly: "pro Jahr", once: "einmalig" };
const STATUS = {
  active: { label: "Laufend", cls: "bg-emerald-50 border-emerald-200 text-emerald-900", Icon: CheckCircle2 },
  ending_soon: { label: "Endet in ≤ 60 Tagen", cls: "bg-amber-50 border-amber-200 text-amber-900", Icon: AlertTriangle },
  notice_due: { label: "Kündigungsfrist läuft", cls: "bg-rose-50 border-rose-200 text-rose-900", Icon: CalendarClock },
  ended: { label: "Beendet", cls: "bg-slate-100 border-slate-200 text-slate-700", Icon: XCircle },
  cancelled: { label: "Gekündigt", cls: "bg-slate-100 border-slate-300 text-slate-700", Icon: XCircle },
};
const EMPTY = { title: "", partner: "", contract_type: "sonstiges", start_date: "", end_date: "", notice_period_months: "", amount: "", interval: "", currency: "CHF", notes: "", file_path: null, file_name: null, mime_type: null, ai_extracted: false, expense_account: "", auto_book_bank: false };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-CH") : "—");

export default function Contracts() {
  const { company } = useCompany();
  const companyId = company?.id || company?._id;
  const [searchParams] = useSearchParams();
  const [scope, setScope] = useState(searchParams.get("scope") === "all" ? "all" : "company");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  useHighlightParam(items, (c) => c.id);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(scope === "all" ? "/contracts" : `/companies/${companyId}/contracts`);
      setItems(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Verträge konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, [companyId, scope]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(c).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? ""])) });
    setDialogOpen(true);
  };
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [matching, setMatching] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/accounts`).then((r) => setExpenseAccounts((r.data || []).filter((a) => a.type === "expense"))).catch(() => {});
  }, [companyId]);
  const matchBank = async (c) => {
    setMatching(c.id);
    try {
      const r = await apiClient.post(`/companies/${c.company_id}/contracts/${c.id}/match-bank`);
      toast.success(r.data.count ? `${r.data.count} Bank-Abbuchung${r.data.count === 1 ? "" : "en"} auf Konto ${r.data.account} gebucht.` : "Keine offenen Abbuchungen mit diesem Betrag gefunden.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen."); }
    finally { setMatching(null); }
  };

  const onExtract = async (file) => {
    if (!file || !companyId) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiClient.post(`/companies/${companyId}/contracts/extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      setForm((p) => ({ ...p, title: d.title || p.title, partner: d.partner || p.partner, contract_type: d.contract_type || p.contract_type,
        start_date: d.start_date || p.start_date, end_date: d.end_date || p.end_date, notice_period_months: d.notice_period_months ?? p.notice_period_months,
        amount: d.amount ?? p.amount, interval: d.interval || p.interval, currency: d.currency || p.currency, notes: d.notes || p.notes,
        file_path: d.file_path, file_name: d.file_name, mime_type: d.mime_type, ai_extracted: true }));
      toast.success("Datei gelesen – bitte Felder prüfen.");
    } catch (e) { toast.error(e.response?.data?.detail || "Lesen fehlgeschlagen."); }
    finally { setExtracting(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Bitte einen Titel eingeben."); return; }
    setSaving(true);
    try {
      const payload = { ...form, title: form.title.trim(), partner: form.partner?.trim() || null, start_date: form.start_date || null, end_date: form.end_date || null,
        notice_period_months: form.notice_period_months === "" ? null : parseInt(form.notice_period_months, 10),
        amount: form.amount === "" ? null : parseFloat(form.amount), interval: form.interval || null, notes: form.notes?.trim() || null,
        expense_account: form.expense_account ? String(form.expense_account).trim().split(" ")[0] : null, auto_book_bank: !!form.auto_book_bank };
      if (editId) {
        const c = items.find((x) => x.id === editId);
        await apiClient.put(`/companies/${c.company_id}/contracts/${editId}`, payload);
        toast.success("Vertrag aktualisiert.");
      } else {
        await apiClient.post(`/companies/${companyId}/contracts`, payload);
        toast.success("Vertrag abgelegt.");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const toggleCancel = async (c) => {
    const next = c.status !== "cancelled";
    if (next && !window.confirm(`Vertrag «${c.title}» als gekündigt markieren? Es werden keine Kündigungs-Erinnerungen mehr gesendet.`)) return;
    try { await apiClient.post(`/companies/${c.company_id}/contracts/${c.id}/cancel`, { cancelled: next }); toast.success(next ? "Als gekündigt markiert." : "Kündigung aufgehoben."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen."); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Vertrag «${c.title}» löschen?`)) return;
    try { await apiClient.delete(`/companies/${c.company_id}/contracts/${c.id}`); toast.success("Gelöscht."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
  };

  const count = useMemo(() => items.length, [items]);

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <FileSignature className="h-7 w-7 text-indigo-600" /> Verträge
          </h1>
          <p className="mt-1 text-sm text-slate-500">Miet-, Leasing-, Abo- und Dienstleistungsverträge ablegen – mit wem, was, Beginn, Ende und Kündigungsfrist. Keine Buchung.</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="contract-new-btn"><Plus className="h-4 w-4 mr-1.5" /> Neuer Vertrag</Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap border-b border-slate-200 pb-3" data-testid="contract-scope">
        {[["company", company?.name || "Diese Firma"], ["all", "Alle Firmen"]].map(([id, label]) => (
          <button key={id} onClick={() => setScope(id)} data-testid={`contract-scope-${id}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${scope === id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500" data-testid="contract-count">{count} Vertr{count === 1 ? "ag" : "äge"}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Verträge …</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center bg-slate-50 border-dashed border-2 border-slate-300" data-testid="contracts-empty">
          <FileSignature className="h-10 w-10 mx-auto text-slate-400 mb-3" />
          <p className="text-slate-700 font-medium">Noch keine Verträge abgelegt</p>
          <p className="text-sm text-slate-500 mt-1">Verträge kommen per Posteingang (E-Mail, WhatsApp, Upload) mit «In Verträge ablegen» hierher – oder über «Neuer Vertrag».</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contract-list">
          {items.map((c) => {
            const st = STATUS[c.status] || STATUS.active;
            return (
              <Card key={c.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`contract-card-${c.id}`} data-highlight-id={c.id}>
                <div className={`rounded-md border px-2 py-1.5 text-xs font-medium flex items-center gap-1.5 mb-3 ${st.cls}`}><st.Icon className="h-3.5 w-3.5" />{st.label}</div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex gap-3 min-w-0">
                    {c.has_file && <DocThumb url={`/companies/${c.company_id}/contracts/${c.id}/file`} title={c.file_name || c.title} onOpen={() => setPreview(c)} className="h-16 w-16" testid={`contract-thumb-${c.id}`} />}
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-900 leading-tight">{c.title}</h3>
                      {c.partner && <p className="text-sm text-slate-600">mit {c.partner}</p>}
                    </div>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-[10px]">{TYPES[c.contract_type] || c.contract_type}</Badge>
                </div>
                {scope === "all" && c.company_name && (
                  <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500"><Building2 className="h-3 w-3" />{c.company_name}</p>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div><div className="text-slate-500">Beginn</div><div className="font-medium text-slate-800">{fmtDate(c.start_date)}</div></div>
                  <div><div className="text-slate-500">Ende</div><div className="font-medium text-slate-800">{c.end_date ? fmtDate(c.end_date) : "unbefristet"}</div></div>
                  <div><div className="text-slate-500">Kündigungsfrist</div><div className="font-medium text-slate-800">{c.notice_period_months ? `${c.notice_period_months} Monate` : "—"}</div>
                    {c.notice_deadline && c.status !== "cancelled" && <div className="text-[11px] text-rose-700" data-testid={`contract-notice-deadline-${c.id}`}>kündigen bis {fmtDate(c.notice_deadline)}</div>}
                  </div>
                  <div><div className="text-slate-500">Betrag</div><div className="font-medium text-slate-800">{c.amount != null ? `${Number(c.amount).toFixed(2)} ${c.currency || "CHF"}${c.interval ? ` ${INTERVALS[c.interval] || ""}` : ""}` : "—"}</div></div>
                </div>
                {c.notes && <p className="mt-2 text-xs text-slate-600 italic border-l-2 border-slate-200 pl-2">{c.notes}</p>}
                {(c.expense_account || c.auto_book_bank) && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-violet-200 bg-violet-50/60 px-2 py-1.5 text-[11px]" data-testid={`contract-autobook-${c.id}`}>
                    <span className="text-violet-900 inline-flex items-center gap-1"><Landmark className="h-3 w-3" />Konto {c.expense_account || "—"}{c.auto_book_bank ? " · Abbuchungen automatisch" : ""}{c.debit_count ? ` · ${c.debit_count} gebucht` : ""}</span>
                    {c.expense_account && c.status !== "cancelled" && (
                      <button type="button" onClick={() => matchBank(c)} disabled={matching === c.id} className="underline text-violet-800 hover:text-violet-950 disabled:opacity-50" data-testid={`contract-match-bank-${c.id}`}>
                        {matching === c.id ? "prüfe …" : "offene Abbuchungen zuordnen"}
                      </button>
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {c.ai_extracted && <Sparkles className="h-3 w-3 text-violet-500" title="KI-gelesen" />}
                    {c.has_file && (
                      <button type="button" onClick={() => setPreview(c)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`contract-view-${c.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Ansehen
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => toggleCancel(c)} data-testid={`contract-cancel-${c.id}`} title={c.status === "cancelled" ? "Kündigung aufheben" : "Als gekündigt markieren – keine Erinnerungen mehr"}>
                      {c.status === "cancelled" ? <Undo2 className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5 text-slate-600" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`contract-edit-${c.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`contract-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <DocPreviewDialog open={!!preview} onClose={() => setPreview(null)} title={preview?.file_name || preview?.title} mimeType={preview?.mime_type}
        url={preview ? `/companies/${preview.company_id}/contracts/${preview.id}/file` : null} testid="contract-preview" />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
          <DialogHeader><DialogTitle>{editId ? "Vertrag bearbeiten" : "Neuer Vertrag"}</DialogTitle></DialogHeader>
          {!editId && (
            <div className="border-2 border-dashed border-indigo-300 bg-indigo-50/50 rounded-lg p-4 text-center cursor-pointer hover:bg-indigo-50"
              onClick={() => fileRef.current?.click()} data-testid="contract-extract-zone">
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={(e) => onExtract(e.target.files?.[0])} data-testid="contract-extract-input" />
              {extracting ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /> : <UploadCloud className="h-6 w-6 mx-auto text-indigo-600" />}
              <p className="text-sm text-slate-700 mt-1">{form.file_name ? `Datei: ${form.file_name}` : "Vertrag (PDF/Foto) hochladen – die KI liest Partner, Laufzeit und Kündigungsfrist"}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2"><Label>Was (Titel) *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z.B. Mietvertrag Büro Bahnhofstrasse" data-testid="contract-input-title" /></div>
            <div><Label>Mit wem (Partner)</Label><Input value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} placeholder="z.B. Immo AG" data-testid="contract-input-partner" /></div>
            <div><Label>Art</Label>
              <select value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="contract-input-type">
                {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><Label>Beginn</Label><SwissDateField  value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="contract-input-start" /></div>
            <div><Label>Ende (leer = unbefristet)</Label><SwissDateField  value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="contract-input-end" /></div>
            <div><Label>Kündigungsfrist (Monate)</Label><Input type="number" min="0" value={form.notice_period_months} onChange={(e) => setForm({ ...form, notice_period_months: e.target.value })} data-testid="contract-input-notice" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label>Betrag</Label><Input type="number" step="0.05" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="contract-input-amount" /></div>
              <div><Label>Intervall</Label>
                <select value={form.interval || ""} onChange={(e) => setForm({ ...form, interval: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="contract-input-interval">
                  <option value="">—</option>{Object.entries(INTERVALS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="col-span-2"><Label>Notizen</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contract-input-notes" /></div>
            <div className="col-span-2 rounded-md border border-violet-200 bg-violet-50/50 p-3 grid grid-cols-2 gap-2">
              <div><Label>Aufwandskonto (Abo-Abbuchung)</Label>
                <select value={form.expense_account || ""} onChange={(e) => setForm({ ...form, expense_account: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="contract-input-expense-account">
                  <option value="">—</option>{expenseAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 pt-6 cursor-pointer">
                <input type="checkbox" checked={!!form.auto_book_bank} onChange={(e) => setForm({ ...form, auto_book_bank: e.target.checked })} className="h-4 w-4 accent-violet-600" data-testid="contract-input-auto-book" />
                Bank-Abbuchungen mit diesem Betrag automatisch buchen (ohne Quittung)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="contract-cancel-btn">Abbrechen</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="contract-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
